import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Download as DownloadIcon,
  CloudUpload as UploadIcon,
} from '@mui/icons-material';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import api from '../../utils/api';

interface Submission {
  credentialType: 'DBS' | 'Insurance' | 'WorkPermit';
  submittedDate: string;
  status: 'Pending' | 'Approved' | 'Rejected';
  submittedBy: 'Counsellor' | 'Admin';
  submittedByUserId?: string;
  isProactiveRenewal: boolean;
  reviewedDate?: string;
  reviewedBy?: string;
  reviewNotes?: string;
  dbsNumber?: string;
  dbsIssueDate?: string; // Issue/print date from certificate
  dbsExpiryDate?: string; // Calculated from issue date (3 years)
  insuranceProvider?: string;
  insuranceNumber?: string;
  insuranceExpiryDate?: string;
  workPermitDetails?: string;
  workPermitExpiryDate?: string;
  workPermitNoExpiry?: boolean;
}

interface Props {
  counsellorId: string;
  onUpdate?: () => void;
}

export default function CredentialReactivationReview({ counsellorId, onUpdate }: Props) {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [reviewDialog, setReviewDialog] = useState<{
    open: boolean;
    index: number | null;
    action: 'approve' | 'reject' | null;
  }>({ open: false, index: null, action: null });
  const [reviewNotes, setReviewNotes] = useState('');
  const [uploadDialog, setUploadDialog] = useState(false);

  // Upload form state
  const [credentialType, setCredentialType] = useState<'DBS' | 'Insurance' | 'WorkPermit'>('DBS');
  const [dbsNumber, setDbsNumber] = useState('');
  const [dbsIssueDate, setDbsIssueDate] = useState<Date | null>(null); // Issue/print date from certificate
  const [insuranceProvider, setInsuranceProvider] = useState('');
  const [insuranceNumber, setInsuranceNumber] = useState('');
  const [insuranceExpiryDate, setInsuranceExpiryDate] = useState<Date | null>(null);
  const [workPermitDetails, setWorkPermitDetails] = useState('');
  const [workPermitExpiryDate, setWorkPermitExpiryDate] = useState<Date | null>(null);
  const [workPermitNoExpiry, setWorkPermitNoExpiry] = useState(false);
  const [certificate, setCertificate] = useState<File | null>(null);
  const [adminNotes, setAdminNotes] = useState('');

  useEffect(() => {
    fetchSubmissions();
  }, [counsellorId]);

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/counsellors/${counsellorId}/credential-submissions`);
      setSubmissions(response.data.submissions || []);
    } catch (err: any) {
      console.error('Error fetching submissions:', err);
      setError('Failed to load credential submissions');
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async () => {
    if (reviewDialog.index === null || !reviewDialog.action) return;

    try {
      setSubmitting(true);
      setError(null);

      await api.post(`/credential-reactivation/review/${counsellorId}/${reviewDialog.index}`, {
        approved: reviewDialog.action === 'approve',
        reviewNotes: reviewNotes,
      });

      setSuccess(`Credential ${reviewDialog.action === 'approve' ? 'approved' : 'rejected'} successfully`);
      setReviewDialog({ open: false, index: null, action: null });
      setReviewNotes('');
      fetchSubmissions();
      if (onUpdate) onUpdate();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to review submission');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownload = async (index: number) => {
    try {
      const response = await api.get(`/credential-reactivation/download/${counsellorId}/${index}`);
      
      if (response.data.url) {
        // Open the presigned URL in a new tab
        window.open(response.data.url, '_blank');
      } else {
        setError('Failed to get download URL');
      }
    } catch (err: any) {
      setError('Failed to download certificate');
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        setError('Please upload a PDF or image file (JPG, PNG)');
        return;
      }
      
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }
      
      setCertificate(file);
      setError(null);
    }
  };

  const handleAdminUpload = async () => {
    if (!certificate) {
      setError('Please upload a certificate');
      return;
    }

    // Validate required fields
    if (credentialType === 'DBS' && (!dbsNumber || !dbsIssueDate)) {
      setError('Please provide DBS number and issue date');
      return;
    }
    if (credentialType === 'Insurance' && (!insuranceProvider || !insuranceNumber || !insuranceExpiryDate)) {
      setError('Please provide insurance provider, number, and expiry date');
      return;
    }
    if (credentialType === 'WorkPermit' && (!workPermitDetails || (!workPermitNoExpiry && !workPermitExpiryDate))) {
      setError('Please provide work permit details and an expiry date, or mark the permit as having no expiry date');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const formData = new FormData();
      formData.append('certificate', certificate);
      formData.append('credentialType', credentialType);
      formData.append('adminNotes', adminNotes);

      if (credentialType === 'DBS') {
        formData.append('dbsNumber', dbsNumber);
        formData.append('dbsIssueDate', dbsIssueDate?.toISOString() || ''); // Backend will calculate expiry (issue + 3 years)
      } else if (credentialType === 'Insurance') {
        formData.append('insuranceProvider', insuranceProvider);
        formData.append('insuranceNumber', insuranceNumber);
        formData.append('insuranceExpiryDate', insuranceExpiryDate?.toISOString() || '');
      } else if (credentialType === 'WorkPermit') {
        formData.append('workPermitDetails', workPermitDetails);
        formData.append('workPermitNoExpiry', String(workPermitNoExpiry));
        formData.append('workPermitExpiryDate', workPermitNoExpiry ? '' : (workPermitExpiryDate?.toISOString() || ''));
      }

      await api.post(`/credential-reactivation/admin-submit/${counsellorId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setSuccess('Credential uploaded successfully');
      setUploadDialog(false);
      resetUploadForm();
      fetchSubmissions();
      if (onUpdate) onUpdate();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to upload credential');
    } finally {
      setSubmitting(false);
    }
  };

  const resetUploadForm = () => {
    setCredentialType('DBS');
    setDbsNumber('');
    setDbsIssueDate(null);
    setInsuranceProvider('');
    setInsuranceNumber('');
    setInsuranceExpiryDate(null);
    setWorkPermitDetails('');
    setWorkPermitExpiryDate(null);
    setWorkPermitNoExpiry(false);
    setCertificate(null);
    setAdminNotes('');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Pending': return 'warning';
      case 'Approved': return 'success';
      case 'Rejected': return 'error';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        borderRadius: '12px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        p: 3,
        mb: 3,
      }}
    >
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" sx={{ color: 'white', fontWeight: 500 }}>
          Credential Reactivation Submissions
        </Typography>
        <Button
          variant="contained"
          startIcon={<UploadIcon />}
          onClick={() => setUploadDialog(true)}
        >
          Upload on Behalf
        </Button>
      </Box>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {error && !reviewDialog.open && !uploadDialog && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {submissions.length === 0 ? (
        <Alert severity="info">No credential submissions found</Alert>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)', fontWeight: 600 }}>Type</TableCell>
                <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)', fontWeight: 600 }}>Submitted</TableCell>
                <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)', fontWeight: 600 }}>By</TableCell>
                <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)', fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)', fontWeight: 600 }}>Details</TableCell>
                <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)', fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {submissions.map((sub, index) => (
                <TableRow key={index} sx={{ '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' } }}>
                  <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>{sub.credentialType}</TableCell>
                  <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                    {new Date(sub.submittedDate).toLocaleDateString()}
                    <Typography variant="caption" display="block" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                      {sub.isProactiveRenewal ? 'Proactive' : 'Post-Suspension'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>{sub.submittedBy}</TableCell>
                  <TableCell>
                    <Chip
                      label={sub.status}
                      color={getStatusColor(sub.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                    {sub.credentialType === 'DBS' && (
                      <>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>#{sub.dbsNumber}</Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                          Issued: {sub.dbsIssueDate && new Date(sub.dbsIssueDate).toLocaleDateString()}
                          {sub.dbsExpiryDate && ` • Expires: ${new Date(sub.dbsExpiryDate).toLocaleDateString()}`}
                        </Typography>
                      </>
                    )}
                    {sub.credentialType === 'Insurance' && (
                      <>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>{sub.insuranceProvider}</Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                          #{sub.insuranceNumber} - Expires: {sub.insuranceExpiryDate && new Date(sub.insuranceExpiryDate).toLocaleDateString()}
                        </Typography>
                      </>
                    )}
                    {sub.credentialType === 'WorkPermit' && (
                      <>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>{sub.workPermitDetails}</Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                          {sub.workPermitNoExpiry
                            ? 'No expiry date (indefinite)'
                            : `Expires: ${sub.workPermitExpiryDate ? new Date(sub.workPermitExpiryDate).toLocaleDateString() : 'not provided'}`}
                        </Typography>
                      </>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Download Certificate">
                        <IconButton size="small" onClick={() => handleDownload(index)}>
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {sub.status === 'Pending' && (
                        <>
                          <Tooltip title="Approve">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => setReviewDialog({ open: true, index, action: 'approve' })}
                            >
                              <ApproveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reject">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setReviewDialog({ open: true, index, action: 'reject' })}
                            >
                              <RejectIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Review Dialog */}
      <Dialog 
        open={reviewDialog.open} 
        onClose={() => setReviewDialog({ open: false, index: null, action: null })} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 3,
          }
        }}
      >
        <DialogTitle sx={{ color: 'rgba(255, 255, 255, 0.95)', fontSize: '1.25rem' }}>
          {reviewDialog.action === 'approve' ? 'Approve' : 'Reject'} Credential Submission
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Review Notes"
            value={reviewNotes}
            onChange={(e) => setReviewNotes(e.target.value)}
            placeholder={`Add notes about this ${reviewDialog.action}...`}
            sx={{ 
              mt: 2,
              '& .MuiOutlinedInput-root': {
                color: 'rgba(255, 255, 255, 0.9)',
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#3b82f6',
                },
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.7)',
              },
              '& .MuiInputBase-input::placeholder': {
                color: 'rgba(255, 255, 255, 0.5)',
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setReviewDialog({ open: false, index: null, action: null })} 
            disabled={submitting}
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleReview}
            variant="contained"
            color={reviewDialog.action === 'approve' ? 'success' : 'error'}
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={20} /> : reviewDialog.action === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Admin Upload Dialog */}
      <Dialog 
        open={uploadDialog} 
        onClose={() => setUploadDialog(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 3,
          }
        }}
      >
        <DialogTitle sx={{ color: 'rgba(255, 255, 255, 0.95)', fontSize: '1.25rem' }}>
          Upload Credential on Behalf of Counsellor
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Credential Type *</InputLabel>
              <Select
                value={credentialType}
                onChange={(e) => setCredentialType(e.target.value as any)}
                label="Credential Type *"
                sx={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  '.MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#3b82f6',
                  },
                  '.MuiSvgIcon-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: 'rgba(30, 41, 59, 0.98)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      maxHeight: 300,
                      '& .MuiMenuItem-root': {
                        color: 'rgba(255, 255, 255, 0.9)',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        },
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(100, 181, 246, 0.2)',
                          '&:hover': {
                            backgroundColor: 'rgba(100, 181, 246, 0.3)',
                          },
                        },
                      },
                    },
                  },
                }}
              >
                <MenuItem value="DBS">DBS Certificate</MenuItem>
                <MenuItem value="Insurance">Professional Indemnity Insurance</MenuItem>
                <MenuItem value="WorkPermit">Work Permit</MenuItem>
              </Select>
            </FormControl>

            {credentialType === 'DBS' && (
              <>
                <TextField
                  fullWidth
                  label="DBS Certificate Number *"
                  value={dbsNumber}
                  onChange={(e) => setDbsNumber(e.target.value)}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: 'rgba(255, 255, 255, 0.9)',
                      '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.5)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#3b82f6',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(255, 255, 255, 0.7)',
                    },
                  }}
                />
                <Box>
                  <Typography variant="body2" sx={{ mb: 1, color: 'rgba(255, 255, 255, 0.7)' }}>
                    DBS Issue Date *
                  </Typography>
                  <div className="relative">
                    <DatePicker
                      selected={dbsIssueDate}
                      onChange={(date) => setDbsIssueDate(date)}
                      dateFormat="dd/MM/yyyy"
                      placeholderText="Select issue date (from certificate)"
                      className="theme-input w-full p-2 pr-10 border rounded-lg"
                      popperClassName="datepicker-popper"
                      popperPlacement="bottom-start"
                      popperModifiers={[
                        {
                          name: 'preventOverflow',
                          options: {
                            rootBoundary: 'viewport',
                            tether: false,
                            altAxis: true,
                          },
                        },
                      ] as any}
                      maxDate={new Date()}
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                    />
                  </div>
                </Box>
              </>
            )}

            {credentialType === 'Insurance' && (
              <>
                <TextField
                  fullWidth
                  label="Insurance Provider *"
                  value={insuranceProvider}
                  onChange={(e) => setInsuranceProvider(e.target.value)}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: 'rgba(255, 255, 255, 0.9)',
                      '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.5)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#3b82f6',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(255, 255, 255, 0.7)',
                    },
                  }}
                />
                <TextField
                  fullWidth
                  label="Policy Number *"
                  value={insuranceNumber}
                  onChange={(e) => setInsuranceNumber(e.target.value)}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: 'rgba(255, 255, 255, 0.9)',
                      '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.5)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#3b82f6',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(255, 255, 255, 0.7)',
                    },
                  }}
                />
                <Box>
                  <Typography variant="body2" sx={{ mb: 1, color: 'rgba(255, 255, 255, 0.7)' }}>
                    Policy Expiry Date *
                  </Typography>
                  <div className="relative">
                    <DatePicker
                      selected={insuranceExpiryDate}
                      onChange={(date) => setInsuranceExpiryDate(date)}
                      dateFormat="dd/MM/yyyy"
                      placeholderText="Select expiry date"
                      className="theme-input w-full p-2 pr-10 border rounded-lg"
                      popperClassName="datepicker-popper"
                      popperPlacement="bottom-start"
                      popperModifiers={[
                        {
                          name: 'preventOverflow',
                          options: {
                            rootBoundary: 'viewport',
                            tether: false,
                            altAxis: true,
                          },
                        },
                      ] as any}
                      minDate={new Date()}
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                    />
                  </div>
                </Box>
              </>
            )}

            {credentialType === 'WorkPermit' && (
              <>
                <TextField
                  fullWidth
                  label="Work Permit Details *"
                  value={workPermitDetails}
                  onChange={(e) => setWorkPermitDetails(e.target.value)}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: 'rgba(255, 255, 255, 0.9)',
                      '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.5)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#3b82f6',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(255, 255, 255, 0.7)',
                    },
                  }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={workPermitNoExpiry}
                      onChange={(e) => {
                        setWorkPermitNoExpiry(e.target.checked);
                        if (e.target.checked) setWorkPermitExpiryDate(null);
                      }}
                      sx={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        '&.Mui-checked': { color: '#3b82f6' },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                      No expiry date (indefinite right to work)
                    </Typography>
                  }
                />
                <Box>
                  <Typography variant="body2" sx={{ mb: 1, color: 'rgba(255, 255, 255, 0.7)' }}>
                    Work Permit Expiry Date {!workPermitNoExpiry && '*'}
                  </Typography>
                  <div className="relative">
                    <DatePicker
                      selected={workPermitExpiryDate}
                      onChange={(date) => setWorkPermitExpiryDate(date)}
                      dateFormat="dd/MM/yyyy"
                      placeholderText={workPermitNoExpiry ? 'Not applicable - no expiry' : 'Select expiry date'}
                      disabled={workPermitNoExpiry}
                      className="theme-input w-full p-2 pr-10 border rounded-lg"
                      popperClassName="datepicker-popper"
                      popperPlacement="bottom-start"
                      popperModifiers={[
                        {
                          name: 'preventOverflow',
                          options: {
                            rootBoundary: 'viewport',
                            tether: false,
                            altAxis: true,
                          },
                        },
                      ] as any}
                      minDate={new Date()}
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                    />
                  </div>
                </Box>
              </>
            )}

            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadIcon />}
              fullWidth
              sx={{
                color: 'white',
                borderColor: 'rgba(255, 255, 255, 0.3)',
                '&:hover': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                },
              }}
            >
              {certificate ? certificate.name : 'Upload Certificate *'}
              <input
                type="file"
                hidden
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleFileChange}
              />
            </Button>

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Admin Notes"
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              placeholder="e.g., Counsellor emailed certificate directly"
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: 'rgba(255, 255, 255, 0.9)',
                  '& fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#3b82f6',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                },
                '& .MuiInputBase-input::placeholder': {
                  color: 'rgba(255, 255, 255, 0.5)',
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setUploadDialog(false)} 
            disabled={submitting}
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAdminUpload}
            variant="contained"
            disabled={submitting || !certificate}
          >
            {submitting ? <CircularProgress size={20} /> : 'Upload & Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
